/**
 * HoursModal - Opening hours editor modal
 * Redesigned with table layout: Day | Opening | Closing | Closed toggle
 */
import { X } from 'lucide-react';
import { useState, useEffect } from 'react';

const DAYS = [
    { key: 'sunday', label: 'Sunday' },
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
];

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeStr;
};

const HoursModal = ({
    workingDaysAndHours,
    onHoursChange,
    onApplyAll,
    onClose,
    isNewRegistration = false,
}) => {
    // Track which days are closed
    const [closedDays, setClosedDays] = useState(() => {
        const closed = {};
        DAYS.forEach(({ key }) => {
            const dayHours = workingDaysAndHours[key];
            // Day is closed if:
            // - No hours set
            // - Explicitly marked as "closed" or "Closed"
            // - Object with no open/close times
            closed[key] = !dayHours || 
                         dayHours === 'closed' || 
                         dayHours === 'Closed' || 
                         (typeof dayHours === 'object' && (!dayHours.open || !dayHours.close || dayHours.open === 'closed'));
        });
        return closed;
    });

    // Set default times if not already set
    const getDefaultTimes = () => {
        const monday = workingDaysAndHours?.monday;
        if (monday && typeof monday === 'object' && monday.open && monday.close) {
            return { open: monday.open, close: monday.close };
        }
        // Default: 9 AM to 5 PM
        return { open: '09:00', close: '17:00' };
    };

    const handleToggleClosed = (day) => {
        const newClosedState = !closedDays[day];
        setClosedDays(prev => ({ ...prev, [day]: newClosedState }));
        
        if (newClosedState) {
            // Mark as closed - send "closed" string
            onHoursChange(day, 'open', 'closed');
            onHoursChange(day, 'close', 'closed');
        } else {
            // Reopen - clear the hours so user can set them
            onHoursChange(day, 'open', '');
            onHoursChange(day, 'close', '');
        }
    };

    const getTimeValues = (day) => {
        if (closedDays[day]) return { openTime: '', closeTime: '' };
        
        const dayHours = workingDaysAndHours[day];
        let openTime = '';
        let closeTime = '';

        if (typeof dayHours === 'object' && dayHours) {
            openTime = dayHours.open || '';
            closeTime = dayHours.close || '';
        } else if (typeof dayHours === 'string' && dayHours.includes('-')) {
            const [open, close] = dayHours.split('-').map(t => t.trim());
            openTime = formatTime(open);
            closeTime = formatTime(close);
        }

        return { openTime, closeTime };
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 p-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '550vh' }}>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl backdrop-blur-xl border border-slate-700 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" style={{ margin: 'auto' }}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-700">
                    <h3 className="text-2xl font-bold text-white">Opening Hours</h3>
                    <button 
                        onClick={onClose} 
                        className="text-slate-400 hover:text-white transition p-2 hover:bg-slate-700 rounded-lg"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Apply to All Section */}
                    <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30 mb-6">
                        <label className="block text-emerald-300 font-semibold mb-3">Apply Same Time to All Days</label>
                        <div className="flex gap-3 items-center">
                            <input
                                type="time"
                                id="applyOpenTime"
                                defaultValue={isNewRegistration ? "09:00" : ""}
                                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30"
                            />
                            <span className="text-slate-400">to</span>
                            <input
                                type="time"
                                id="applyCloseTime"
                                defaultValue={isNewRegistration ? "17:00" : ""}
                                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30"
                            />
                            <button
                                onClick={() => {
                                    const openTime = document.getElementById('applyOpenTime').value || (isNewRegistration ? '09:00' : '');
                                    const closeTime = document.getElementById('applyCloseTime').value || (isNewRegistration ? '17:00' : '');
                                    if (openTime && closeTime) {
                                        onApplyAll(openTime, closeTime);
                                        // Uncheck all closed toggles
                                        const newClosed = {};
                                        DAYS.forEach(({ key }) => { newClosed[key] = false; });
                                        setClosedDays(newClosed);
                                    }
                                }}
                                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition whitespace-nowrap"
                            >
                                Apply All
                            </button>
                        </div>
                    </div>

                    {/* Table Header */}
                    <div className="bg-slate-700/50 rounded-t-xl border border-slate-600">
                        <div className="grid grid-cols-[1.5fr,2fr,2fr,1fr] gap-4 px-6 py-4 font-bold text-white text-sm">
                            <div>Day</div>
                            <div>Opening</div>
                            <div>Closing</div>
                            <div className="text-center">Closed</div>
                        </div>
                    </div>

                    {/* Table Rows */}
                    <div className="border-x border-b border-slate-600 rounded-b-xl overflow-hidden">
                        {DAYS.map(({ key, label }, index) => {
                            const { openTime, closeTime } = getTimeValues(key);
                            const isClosed = closedDays[key];
                            
                            return (
                                <div 
                                    key={key}
                                    className={`grid grid-cols-[1.5fr,2fr,2fr,1fr] gap-4 px-6 py-4 items-center ${
                                        index % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-800/50'
                                    } ${index !== DAYS.length - 1 ? 'border-b border-slate-700/50' : ''}`}
                                >
                                    <div className="text-white font-medium">{label}</div>
                                    
                                    <input
                                        type="time"
                                        value={openTime}
                                        onChange={(e) => {
                                            onHoursChange(key, 'open', e.target.value);
                                            if (e.target.value && closedDays[key]) {
                                                setClosedDays(prev => ({ ...prev, [key]: false }));
                                            }
                                        }}
                                        disabled={isClosed}
                                        className={`px-3 py-2 rounded-lg text-white focus:outline-none transition ${
                                            isClosed 
                                                ? 'bg-slate-700/30 border border-slate-600/30 text-slate-500 cursor-not-allowed' 
                                                : 'bg-slate-700 border border-slate-600 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30'
                                        }`}
                                    />
                                    
                                    <input
                                        type="time"
                                        value={closeTime}
                                        onChange={(e) => {
                                            onHoursChange(key, 'close', e.target.value);
                                            if (e.target.value && closedDays[key]) {
                                                setClosedDays(prev => ({ ...prev, [key]: false }));
                                            }
                                        }}
                                        disabled={isClosed}
                                        className={`px-3 py-2 rounded-lg text-white focus:outline-none transition ${
                                            isClosed 
                                                ? 'bg-slate-700/30 border border-slate-600/30 text-slate-500 cursor-not-allowed' 
                                                : 'bg-slate-700 border border-slate-600 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30'
                                        }`}
                                    />
                                    
                                    <div className="flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleClosed(key)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                                                isClosed ? 'bg-slate-600' : 'bg-emerald-600'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                    isClosed ? 'translate-x-1' : 'translate-x-6'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-semibold rounded-xl transition shadow-lg hover:shadow-emerald-500/50"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HoursModal;
